import React, { useState, useEffect, useCallback, useRef } from 'react';

interface ResizableHandleProps {
  onResize: (e: MouseEvent, delta: number) => void;
  onResizeStart?: () => void;
  direction?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * 可拖拽分隔线组件 - 优化版本
 * 参考 makeverse.cn 的交互设计，提升拖动灵敏度
 */
export const ResizableHandle: React.FC<ResizableHandleProps> = ({
  onResize,
  onResizeStart,
  direction = 'horizontal',
  className = '',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  // 使用 ref 来跟踪拖动状态和起始位置，避免闭包问题
  const isDraggingRef = useRef(false);
  const startPosRef = useRef(0);
  const startValueRef = useRef(0);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;

    // 计算相对于起始位置的偏移量
    const currentPos = direction === 'horizontal' ? e.clientX : e.clientY;
    const delta = currentPos - startPosRef.current;

    // 直接调用 onResize，不使用 requestAnimationFrame 节流
    // 提升拖动灵敏度
    onResizeRef.current(e, delta);
  }, [direction]);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // 拖拽时禁用文本选择
    document.body.style.userSelect = 'none';
    document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // 恢复文本选择和光标
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp, direction]);

  const directionStyles = direction === 'horizontal'
    ? 'w-2 cursor-col-resize'
    : 'h-2 cursor-row-resize';

  // 视觉状态样式
  const getVisualStyles = () => {
    if (isDragging) {
      return 'bg-ink-800 shadow-[0_0_8px_rgba(99,102,241,0.3)] z-50';
    }
    if (isHovered) {
      return 'bg-gray-400';
    }
    return 'bg-gray-200';
  };

  // 内部线条样式
  const getInnerLineStyles = () => {
    if (isDragging) {
      return 'bg-agent-400';
    }
    if (isHovered) {
      return 'bg-gray-300';
    }
    return 'bg-transparent';
  };

  return (
    <div
      className={`
        ${directionStyles}
        ${getVisualStyles()}
        flex items-center justify-center
        transition-all duration-150 ease-out
        hover:scale-x-150
        ${className}
      `}
      onMouseDown={(e) => {
        isDraggingRef.current = true;
        startPosRef.current = direction === 'horizontal' ? e.clientX : e.clientY;
        setIsDragging(true);
        onResizeStart?.();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="separator"
      aria-orientation={direction}
      aria-label={direction === 'horizontal' ? '调整列宽' : '调整行高'}
      style={{ willChange: 'transform' }}
    >
      {/* 内部装饰线条 */}
      <div 
        className={`
          ${direction === 'horizontal' ? 'w-0.5 h-8' : 'w-8 h-0.5'}
          rounded-full
          ${getInnerLineStyles()}
          transition-colors duration-150
        `}
      />
    </div>
  );
};

export default ResizableHandle;
